import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAppData } from '../hooks/useAppData';
import { Lock, User, Check, ArrowRight, Building, BookOpen } from 'lucide-react';
import { getErrorMessage } from '../utils/errors';

const TherapistOnboarding: React.FC = () => {
    const { profile } = useAppData();
    const [fullName, setFullName] = useState(profile?.full_name || '');
    const [clinicName, setClinicName] = useState('');
    const [specialization, setSpecialization] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (newPassword.length > 0 || confirmPassword.length > 0) {
            if (newPassword.length < 6) { setError('הסיסמה חייבת להכיל 6 תווים לפחות.'); return; }
            if (newPassword !== confirmPassword) { setError('הסיסמאות אינן תואמות.'); return; }
        }

        setLoading(true);

        try {
            // 1. עדכון סיסמה אם הוזנה
            if (newPassword) {
                const { error: passError } = await supabase.auth.updateUser({
                    password: newPassword
                });
                if (passError) throw passError;
            }

            let clinicId = profile?.clinic_id;

            // 2. יצירת מרפאה חדשה אם הוזן שם (בהנחה ויש הרשאת Insert)
            if (clinicName) {
                const { data: clinicData, error: clinicError } = await supabase
                    .from('clinics')
                    .insert([{ name: clinicName, subscription_tier: 'free' }])
                    .select('id')
                    .single();
                
                if (!clinicError && clinicData) {
                    clinicId = clinicData.id;
                } else if (clinicError) {
                    console.warn('Failed to create clinic, might be missing RLS policy or table', clinicError);
                }
            }

            // 3. עדכון פרופיל המטפל
            const { error: profileError } = await supabase
                .from('profiles')
                .update({
                    full_name: fullName,
                    issue_description: specialization, // משתמשים בשדה קיים לבינתיים
                    clinic_id: clinicId,
                    first_login: false
                })
                .eq('id', profile?.id);

            if (profileError) throw profileError;

            alert("ברוך הבא! הפרטים עודכנו בהצלחה.");
            window.location.reload();

        } catch (err) {
            console.error(err);
            setError('שגיאה בעדכון: ' + getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="h-full w-full overflow-y-auto bg-card font-sans scrollbar-hide" dir="rtl">
            <div className="min-h-full flex flex-col items-center justify-center p-4 py-8">
                <div className="w-full max-w-md bg-muted p-8 rounded-3xl border border-border shadow-2xl animate-fade-in relative">
                <header className="text-center mb-8 relative z-10">
                    <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">ברוך הבא ל-ReMind</h1>
                    <p className="text-muted-foreground text-sm max-w-xs mx-auto leading-relaxed">
                        בוא נגדיר את הקליניקה שלך כדי שתוכל להתחיל לעבוד.
                    </p>
                </header>

                <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                    <div>
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">שם מלא</label>
                        <div className="relative group">
                            <User className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                            <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="w-full bg-card/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-card focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="שם המטפל"
                                required
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">שם הקליניקה (אופציונלי)</label>
                        <div className="relative group">
                            <Building className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                            <input
                                type="text"
                                value={clinicName}
                                onChange={(e) => setClinicName(e.target.value)}
                                className="w-full bg-card/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-card focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="למשל: קליניקת השלווה"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">התמחות (אופציונלי)</label>
                        <div className="relative group">
                            <BookOpen className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                            <input
                                type="text"
                                value={specialization}
                                onChange={(e) => setSpecialization(e.target.value)}
                                className="w-full bg-card/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-card focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="למשל: טיפול התנהגותי קוגניטיבי"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t border-border">
                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">סיסמה חדשה (השאר ריק כדי לא לשנות)</label>
                        <div className="relative group mb-4">
                            <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={20} />
                            <input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full bg-card/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-card focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="לפחות 6 תווים"
                            />
                        </div>

                        <label className="block text-muted-foreground text-sm font-medium mb-1.5 mr-1">אימות סיסמה חדשה</label>
                        <div className="relative group">
                            <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-green-500 transition-colors" size={20} />
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full bg-card/50 border border-border/50 rounded-xl py-3.5 pr-11 pl-4 text-foreground placeholder:text-muted-foreground focus:bg-card focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all text-right font-medium"
                                placeholder="הקלד שוב את הסיסמה"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-500/10 text-red-400 text-sm p-4 rounded-xl border border-red-500/20 text-center animate-shake">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 mt-6 shadow-lg shadow-primary/25"
                    >
                        {loading ? 'שומר פרטים...' : (
                            <>
                                כניסה למערכת <ArrowRight size={20} className="rotate-180" />
                            </>
                        )}
                    </button>
                </form>
            </div>
            </div>
        </div>
    );
};

export default TherapistOnboarding;
